/**
 * The optimization run kept across a page reload (AIT-426): one versioned record instead
 * of five loose `optimization_run_*` keys. Only the WebSocket service writes it; pages
 * read it through the service's getters. Cleared when the run ends or the user signs out
 * (performGlobalSignoutCleanup → disconnect(true)).
 */

export const RUN_SESSION_KEY = 'optimization_run:v1';

/** Keys of the pre-v1 layout: migrated into the record once, then removed. */
const LEGACY_KEYS = {
  runId: 'optimization_run_id',
  mode: 'optimization_run_mode',
  iterations: 'optimization_run_iterations',
  progress: 'optimization_run_progress',
  isConnector: 'optimization_run_is_connector',
} as const;

export interface RunSession {
  runId: string;
  mode?: string | null;
  iterations?: number;
  progress?: number;
  isConnector?: boolean;
}

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

function migrateLegacy(store: Storage): RunSession | null {
  const runId = store.getItem(LEGACY_KEYS.runId);
  const session: RunSession | null = runId
    ? {
        runId,
        mode: store.getItem(LEGACY_KEYS.mode),
        iterations: Number(store.getItem(LEGACY_KEYS.iterations)) || undefined,
        progress: Number(store.getItem(LEGACY_KEYS.progress)) || undefined,
        isConnector: store.getItem(LEGACY_KEYS.isConnector) === 'true',
      }
    : null;
  Object.values(LEGACY_KEYS).forEach((key) => store.removeItem(key));
  if (session) store.setItem(RUN_SESSION_KEY, JSON.stringify(session));
  return session;
}

export function readRunSession(): RunSession | null {
  const store = storage();
  if (!store) return null;
  const raw = store.getItem(RUN_SESSION_KEY);
  if (!raw) return migrateLegacy(store);
  try {
    const parsed = JSON.parse(raw) as RunSession;
    return parsed && typeof parsed.runId === 'string' && parsed.runId ? parsed : null;
  } catch {
    store.removeItem(RUN_SESSION_KEY);
    return null;
  }
}

/** Starts a record for a new run (replacing any previous one). */
export function startRunSession(session: RunSession): void {
  storage()?.setItem(RUN_SESSION_KEY, JSON.stringify(session));
}

/** Updates fields of the current run; does nothing when no run is stored. */
export function updateRunSession(patch: Partial<Omit<RunSession, 'runId'>>): void {
  const current = readRunSession();
  if (!current) return;
  storage()?.setItem(RUN_SESSION_KEY, JSON.stringify({ ...current, ...patch }));
}

export function clearRunSession(): void {
  const store = storage();
  if (!store) return;
  store.removeItem(RUN_SESSION_KEY);
  Object.values(LEGACY_KEYS).forEach((key) => store.removeItem(key));
}
